// Mock implementation of the GoTo SMS conversation contract (spec §4).
// Deleted (along with USE_GOTO_MOCKS) once the Laravel + GoTo webhook ship.
import moment from "moment";

const at = (daysAgo, time) =>
  moment(`${moment().subtract(daysAgo, "days").format("YYYY-MM-DD")}T${time}`).toISOString();

let mockSeq = 1000;

// Threads keyed by customer_id (churn mock customers).
const threads = {
  9005: [
    { id: "m_1", direction: "outbound", body: "Hi Ava! It's been a while since your last Hydrafacial — we'd love to see you again. Want me to find you a spot this week?", media: [], created_at: at(18, "11:02:00"), status: "delivered", actor_name: "Sam Reyes" },
    { id: "m_2", direction: "inbound", body: "Oh hi! Yes I've been meaning to book. What do you have Thursday?", media: [], created_at: at(18, "11:20:00"), status: "received", actor_name: null },
    { id: "m_3", direction: "outbound", body: "Thursday we have 10:30am or 2pm with Priya. Which works better?", media: [], created_at: at(18, "11:24:00"), status: "delivered", actor_name: "Sam Reyes" },
    { id: "m_4", direction: "inbound", body: "2pm please!", media: [], created_at: at(18, "11:31:00"), status: "received", actor_name: null },
    { id: "m_5", direction: "outbound", body: "You're booked for Thursday 2pm. See you then!", media: [], created_at: at(18, "11:33:00"), status: "delivered", actor_name: "Sam Reyes" },
  ],
  9007: [
    { id: "m_10", direction: "outbound", body: "Hi Mason, checking in — you're due for your usual visit. Should I hold a spot for you next week?", media: [], created_at: at(25, "09:15:00"), status: "delivered", actor_name: "Priya Shah" },
    { id: "m_11", direction: "inbound", body: "Thanks for the reminder. Next Tuesday afternoon?", media: [], created_at: at(25, "10:02:00"), status: "received", actor_name: null },
  ],
  9001: [
    { id: "m_20", direction: "inbound", body: "Hi! Can I move my appointment to Friday?", media: [], created_at: at(3, "14:48:00"), status: "received", actor_name: null },
    { id: "m_21", direction: "outbound", body: "Of course, Emma — moved you to Friday 11am. Confirmation on its way.", media: [], created_at: at(3, "14:52:00"), status: "delivered", actor_name: "Sam Reyes" },
  ],
  9002: [
    { id: "m_30", direction: "outbound", body: "Hi Liam, your package of aftercare products is ready for pickup at the front desk.", media: [], created_at: at(1, "10:05:00"), status: "delivered", actor_name: "Priya Shah" },
    { id: "m_31", direction: "inbound", body: "Great, I'll swing by after work today. Thanks!", media: [], created_at: at(1, "10:22:00"), status: "received", actor_name: null },
  ],
  9003: [
    { id: "m_40", direction: "outbound", body: "Olivia, just confirming your facial for tomorrow 3pm.", media: [], created_at: at(0, "09:12:00"), status: "delivered", actor_name: "Sam Reyes" },
    { id: "m_41", direction: "inbound", body: "Confirmed! Also — do you carry the SPF you used last time?", media: [], created_at: at(0, "09:31:00"), status: "received", actor_name: null },
  ],
  9008: [
    { id: "m_50", direction: "outbound", body: "Hi Isabella, we miss you! Your membership pricing is still locked in if you'd like to come back.", media: [], created_at: at(6, "15:40:00"), status: "delivered", actor_name: "Priya Shah" },
    { id: "m_51", direction: "inbound", body: "Oh that's good to know. What would a reactivation look like?", media: [], created_at: at(6, "16:05:00"), status: "received", actor_name: null },
  ],
};

// Conversations whose latest inbound hasn't been seen yet (red dot).
const unreadSet = new Set(["9003", "9008"]);

// The mock "authenticated staff" for the Mine quick filter.
const MOCK_ME = "Sam Reyes";

const staffFor = (rows) => {
  const lastOutbound = [...rows].reverse().find((m) => m.direction === "outbound");
  return lastOutbound?.actor_name || MOCK_ME;
};

// Live-reply simulator: ChatModal's hook registers a listener per customer;
// mockSendGoto pings it ~4s after a send so realtime is demoable.
const listeners = {};

export const mockSubscribe = (customerId, callback) => {
  const key = String(customerId);
  listeners[key] = listeners[key] || [];
  listeners[key].push(callback);
  return () => {
    listeners[key] = (listeners[key] || []).filter((fn) => fn !== callback);
  };
};

// Tenant-wide inbox listeners (spec §6.2) — the board subscribes once and
// receives {customer_id, message} for any conversation.
const inboxListeners = [];

export const mockSubscribeInbox = (callback) => {
  inboxListeners.push(callback);
  return () => {
    const index = inboxListeners.indexOf(callback);
    if (index >= 0) inboxListeners.splice(index, 1);
  };
};

const CANNED_REPLIES = [
  "Sounds great, thank you!",
  "Perfect, I'll be there.",
  "Thanks for letting me know!",
];

export const mockGetConversation = (params = {}) => {
  const rows = threads[params.customer_id] || [];
  return {
    messages: [...rows],
    has_more: false,
    oldest_cursor: rows[0]?.id || null,
    unread: false,
  };
};

export const mockSendGoto = (formData) => {
  const customer_id = formData.get("customer_id");
  const body = formData.get("body") || "";
  const mediaFiles = formData.getAll("media[]");
  const row = {
    id: `m_${mockSeq++}`,
    direction: "outbound",
    body,
    media: mediaFiles.map((file) => URL.createObjectURL(file)),
    created_at: moment().toISOString(),
    status: "sent",
    actor_name: "You",
  };
  threads[customer_id] = threads[customer_id] || [];
  threads[customer_id].push(row);

  setTimeout(() => {
    const reply = {
      id: `m_${mockSeq++}`,
      direction: "inbound",
      body: CANNED_REPLIES[mockSeq % CANNED_REPLIES.length],
      media: [],
      created_at: moment().toISOString(),
      status: "received",
      actor_name: null,
    };
    threads[customer_id].push(reply);
    (listeners[String(customer_id)] || []).forEach((fn) => fn(reply));
    inboxListeners.forEach((fn) => fn({ customer_id: Number(customer_id), message: reply }));
  }, 4000);

  return row;
};

export const mockMarkRead = (customerId) => {
  unreadSet.delete(String(customerId));
  return { ok: true };
};

// Inbox list (spec §6.1). Joins churn mock customers with their threads.
export const mockGetInbox = (params = {}, customerRows = []) => {
  const { search, staff_id, quick } = params;
  let rows = Object.keys(threads)
    .map((key) => {
      const messages = threads[key];
      const last = messages[messages.length - 1];
      const customer =
        customerRows.find((r) => String(r.user_id) === String(key)) || {};
      return {
        user_id: Number(key),
        name: customer.name || `Customer ${key}`,
        phone: customer.phone || "",
        email: customer.email || "",
        customer_batch: customer.customer_batch || "New User",
        staff_name: staffFor(messages),
        unread: unreadSet.has(String(key)),
        last_message: last
          ? { direction: last.direction, body: last.body, created_at: last.created_at }
          : null,
      };
    })
    .filter((row) => row.last_message);

  if (search) {
    const term = search.toLowerCase();
    rows = rows.filter(
      (row) =>
        row.name.toLowerCase().includes(term) ||
        row.email.toLowerCase().includes(term) ||
        row.phone.replace(/\s/g, "").includes(search.replace(/\s/g, ""))
    );
  }
  if (staff_id) {
    // Mock has no staff ids on threads; treat any staff filter as "mine".
    rows = rows.filter((row) => row.staff_name === MOCK_ME);
  }
  if (quick === "unread") rows = rows.filter((row) => row.unread);
  if (quick === "awaiting_reply")
    rows = rows.filter((row) => row.last_message.direction === "inbound");
  if (quick === "mine") rows = rows.filter((row) => row.staff_name === MOCK_ME);

  rows.sort(
    (a, b) => moment(b.last_message.created_at).valueOf() - moment(a.last_message.created_at).valueOf()
  );
  return { rows, has_more: false };
};
